/**
 * Unified Notification System
 *
 * This module exports all notification utilities for the application.
 *
 * Usage:
 *   // Simple toast notifications (no persistence, no hooks required)
 *   import { notify } from '@/lib/notifications';
 *
 *   notify.success('Saved!')
 *   notify.error('Failed to save')
 *   notify.loading('Saving...')
 *   notify.info('FYI...')
 *   notify.warning('Be careful!')
 *   notify.dismiss(toastId)
 *   notify.message({ sender: 'John', message: 'Hello!' })
 *
 *   // Persistent notifications with database storage (requires hooks)
 *   import { useUnifiedNotifications } from '@/hooks/useUnifiedNotifications';
 *
 *   // Advanced utilities
 *   import { checkDedupe, checkRateLimit } from '@/lib/notifications';
 */

// Simple toast service (no hooks required)
export { notify } from './toast';
export { showUnifiedToast } from './show-unified-toast';
export type { ShowToastOptions } from './show-unified-toast';
export type {
  ToastOptions,
  RichMessageOptions,
  NotifyInstance,
} from './toast-types';

// Types and rules
export {
  type NotifyOptions,
  type ShorthandOptions,
  type NotificationPriority,
  type ToastStyle,
  type NotificationScope,
  type SessionType,
  type NotificationSession,
  type DedupeResult,
  type RateLimitResult,
  type RateLimitConfig,
  type NotificationRule,
  NOTIFICATION_RULES,
  DEFAULT_RATE_LIMITS,
  DEFAULT_DEDUPE_WINDOW_MS,
  DEDUPE_WINDOWS,
  TOAST_DURATIONS,
  getNotificationRule,
  getDedupeWindow,
  getEffectiveScope,
  shouldStore,
} from './types';

// Deduplication
export {
  NotificationDeduplicator,
  getDeduplicator,
  checkDedupe,
  flushDedupe,
  clearDedupe,
} from './deduplication';

// Rate limiting
export {
  NotificationRateLimiter,
  getRateLimiter,
  checkRateLimit,
  recordNotification,
  enqueueNotification,
} from './rateLimiter';

// Session storage (for scope: 'session' notifications)
export {
  type SessionNotification,
  addSessionNotification,
  getSessionNotifications,
  getNotificationsByType,
  getUnreadCount,
  markAsRead,
  markSessionAsRead,
  clearSession,
  clearSessionType,
  clearAllSessions,
  cleanupOldNotifications,
  subscribeToSession,
} from './sessionStore';
